/**
 * Mostrar métrica sem transformar ausência em afirmação.
 *
 * Nasce de um defeito medido em produção: `budget.data?.current_cost_usd ?? 0` fazia o painel de
 * gestor mostrar **"$0,00 / $5.000" com a barra verde e "0,0% utilizado"** quando a API de custo
 * caía. Zero é pior que uma lista vazia porque é **plausível** — entra na coluna, aceita duas casas
 * decimais e pinta de verde.
 *
 * E neste produto zero não é neutro: em custo, monitoramento e segurança, zero lê como "está tudo
 * bem". O falso "tudo certo" é o pior desfecho, porque não gera chamado nem reclamação.
 *
 * Levantamento: `docs/superpowers/plans/2026-08-14-erro-como-zero.md`.
 */

/** Travessão, e não "0", "N/A" ou vazio: ocupa o lugar do número sem afirmar um. */
export const SEM_VALOR = '—'

/** `null`, `undefined` e `NaN`. O terceiro escapa de `??` e viraria "NaN" na tela. */
function ausente(valor: number | null | undefined): boolean {
  return valor === null || valor === undefined || Number.isNaN(valor)
}

/**
 * Formata quando há valor; devolve `—` quando não há.
 *
 * **Zero legítimo continua zero**: uma conta que gastou nada gastou nada, e esconder isso trocaria
 * um defeito por outro.
 */
export function metrica(
  valor: number | null | undefined,
  formatar: (v: number) => string,
): string {
  return ausente(valor) ? SEM_VALOR : formatar(valor as number)
}

export interface FaixaDeStatus {
  /** Limite superior **inclusivo** da faixa. Use `Infinity` na última. */
  ate: number
  cor: string
}

/**
 * Cor de status para um valor — e **`undefined` quando o valor não veio**.
 *
 * Esta é a regra inteira, e o motivo de o helper existir: verde, amarelo e vermelho **afirmam algo
 * sobre o mundo**. Quando não se sabe, a cor certa é nenhuma. Pintar de verde o que não foi medido
 * é dizer ao usuário que está tudo bem sem ter olhado.
 */
export function corDeStatus(
  valor: number | null | undefined,
  faixas: FaixaDeStatus[],
): string | undefined {
  if (ausente(valor)) return undefined
  return faixas.find((f) => (valor as number) <= f.ate)?.cor
}
